import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

// UI
import { Box } from '@chakra-ui/react'

import FullTable from '@/components/Table/FullTable/FullTable'

import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
//helper
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import useUser from '@/features/users/useUser'
import { createColumnHelper } from '@tanstack/react-table'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import CostCenterListHeader from '../components/CostCenterListHeader'
import getCostCenterTableColumns from '../constants/costCenterTableColumns'
import { useCostCenter } from '../useCostCenter'
import { LoadingSection } from '@/components/Elements'

const CostCenterListPage = () => {
  // Table State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:costCenter`, {})

  const query = convertTableStateToQuery(storedTableState)

  // Hooks
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const columnHelper = createColumnHelper()

  const { useFetchAllCostCenters, useCreateCostCenter, useRemoveCostCenter, useUpdateCostCenter } = useCostCenter()
  const { useFetchAllUsers, useUserOptions } = useUser()

  const { data: costCentersResponse, isLoading, refetch: refetchCostCenter } = useFetchAllCostCenters(query)

  const removeCostCenter = useRemoveCostCenter()
  const updateCostCenter = useUpdateCostCenter()

  //Translated

  const { data: procurementUserOptions } = useUserOptions({
    contributorGroup: ContributorGroupType.PROCUREMENT_OFFICER,
  })
  const { data: teamAssistantUserOptions } = useUserOptions({ contributorGroup: ContributorGroupType.TEAM_ASSISTANT })

  // Handler

  const handleDetail = (row) => {
    const costCenterId = row?.original?._id
    navigate(`/admin/costCenters/${costCenterId}`)
  }

  const handleDeleteCostCenter = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())

        await removeCostCenter.mutateAsync(id)
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, removeCostCenter]
  )

  const handleActivateCostCenter = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())
        await updateCostCenter.mutateAsync({ id, data: { isDeleted: false } })
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, updateCostCenter]
  )

  const defaultColumns = getCostCenterTableColumns({
    handleActivateCostCenter,
    handleDeleteCostCenter,
    procurementUserOptions,
    teamAssistantUserOptions,
  })

  if (isLoading) {
    return <LoadingSection />
  }

  return (
    <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
      <CostCenterListHeader />
      <Box p={5}>
        <FullTable
          data={costCentersResponse?.data ? costCentersResponse.data : []}
          rowCount={costCentersResponse?.count}
          columnDef={defaultColumns}
          state={storedTableState}
          setState={setStoredTableState}
          handleRowClick={handleDetail}
        />
      </Box>
    </Box>
  )
}

export default CostCenterListPage
